import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
} from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import Sidebar from "./AdminPanel/Sidebar";
import TopBar from "./AdminPanel/TopBar";
import { useDashboardTheme } from "../../context/DashboardThemeContext";
import { useAlert } from "../../context/AlertContext";

function today() {
  return new Date().toISOString().slice(0, 10);
}

function daysAgo(days) {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
}

function formatMoney(value) {
  const number = Number(value || 0);

  return number.toLocaleString("es-EC", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString("es-EC", {
    maximumFractionDigits: 2,
  });
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.2,
        borderRadius: "12px",
        border: "1px solid rgba(148,163,184,0.35)",
        bgcolor: "#FFFFFF",
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#111827", mb: 0.4 }}>
        {label}
      </Typography>

      {payload.map((item) => (
        <Typography key={item.dataKey} sx={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
          {item.name || item.dataKey}:{" "}
          {item.dataKey === "total" || item.dataKey === "totalFacturado" || item.dataKey === "totalCaja"
            ? `$${formatMoney(item.value)}`
            : formatNumber(item.value)}
        </Typography>
      ))}
    </Paper>
  );
}

function StatCard({ title, value, dashboardTheme }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.8, md: 2.2 },
        borderRadius: "20px",
        border: `1px solid ${dashboardTheme.border_color}`,
        bgcolor: dashboardTheme.card_background,
        minHeight: 112,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        boxShadow: "none",
      }}
    >
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 600,
          color: dashboardTheme.muted_text_color,
          mb: 0.8,
          lineHeight: 1.25,
        }}
      >
        {title}
      </Typography>

      <Typography
        sx={{
          fontSize: { xs: 22, md: 25 },
          fontWeight: 700,
          color: dashboardTheme.text_color,
          lineHeight: 1.15,
        }}
      >
        {value}
      </Typography>
    </Paper>
  );
}

function ChartCard({ title, children, dashboardTheme, height = 280 }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.6, md: 2.2 },
        borderRadius: "22px",
        border: `1px solid ${dashboardTheme.border_color}`,
        bgcolor: dashboardTheme.card_background,
        minHeight: height + 78,
        boxShadow: "none",
      }}
    >
      <Typography
        sx={{
          fontSize: { xs: 17, md: 19 },
          fontWeight: 700,
          color: dashboardTheme.text_color,
          mb: 2,
          lineHeight: 1.2,
        }}
      >
        {title}
      </Typography>

      <Box sx={{ width: "100%", height }}>{children}</Box>
    </Paper>
  );
}

export default function AdminStatistics() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { dashboardTheme } = useDashboardTheme();
  const { showError } = useAlert();
  const isMobile = useMediaQuery("(max-width:900px)");

  const [desde, setDesde] = useState(daysAgo(29));
  const [hasta, setHasta] = useState(today());
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const token = localStorage.getItem("accessToken");

  const chartColor = dashboardTheme.primary_color;
  const secondaryColor =
    dashboardTheme.theme_mode === "dark" ? "rgba(255,255,255,0.68)" : "#64748B";
  const gridColor =
    dashboardTheme.theme_mode === "dark" ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.10)";

  const colors = useMemo(
    () => [
      dashboardTheme.primary_color,
      dashboardTheme.button_color,
      dashboardTheme.table_header_color,
      dashboardTheme.sidebar_color,
      dashboardTheme.theme_mode === "dark" ? "#94A3B8" : "#475569",
      dashboardTheme.theme_mode === "dark" ? "#CBD5E1" : "#334155",
    ],
    [dashboardTheme]
  );

  const loadStatistics = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();

      if (desde) params.append("desde", desde);
      if (hasta) params.append("hasta", hasta);

      const response = await fetch(`/api/ventas/statistics/dashboard/?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (!response.ok) {
        showError(result.detail || result.error || "No se pudieron cargar las estadísticas");
        return;
      }

      setData(result);
    } catch {
      showError("Error de conexión");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatistics();
  }, []);

  useEffect(() => {
    const delay = setTimeout(() => {
      loadStatistics();
    }, 350);

    return () => clearTimeout(delay);
  }, [desde, hasta]);

  const resumen = data?.resumen || {};

  const ventasComparadas = useMemo(() => {
    const map = new Map();

    (data?.facturas_por_dia || []).forEach((item) => {
      map.set(item.fecha, {
        fecha: item.fecha,
        totalFacturado: Number(item.total || 0),
        totalCaja: 0,
      });
    });

    (data?.caja_por_dia || []).forEach((item) => {
      const current = map.get(item.fecha) || {
        fecha: item.fecha,
        totalFacturado: 0,
        totalCaja: 0,
      };

      current.totalCaja = Number(item.total || 0);
      map.set(item.fecha, current);
    });

    return Array.from(map.values()).sort((a, b) => a.fecha.localeCompare(b.fecha));
  }, [data]);

  const tableCellSx = {
    color: dashboardTheme.text_color,
    borderBottom: `1px solid ${dashboardTheme.border_color}`,
    fontSize: 13.5,
    fontWeight: 500,
  };

  const emptyBox = (
    <Box
      sx={{
        height: "100%",
        minHeight: 180,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: dashboardTheme.muted_text_color,
        fontSize: 14,
        fontWeight: 600,
        textAlign: "center",
      }}
    >
      {loading ? "Cargando estadísticas..." : "Sin datos para mostrar"}
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: dashboardTheme.dashboard_background,
        display: "flex",
        color: dashboardTheme.text_color,
        overflowX: "hidden",
      }}
    >
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <TopBar
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen((prev) => !prev)}
        />

        <Box
          component="main"
          sx={{
            flex: 1,
            width: "100%",
            p: { xs: 1.5, sm: 2, md: 3 },
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", lg: "95%" },
              mx: "auto",
              mt: { xs: 1, md: 1.5 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "stretch", md: "center" },
                justifyContent: "space-between",
                flexDirection: { xs: "column", md: "row" },
                gap: 1.5,
                mb: { xs: 1.5, md: 2 },
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: 24, sm: 26, md: 30 },
                  fontWeight: 600,
                  color: dashboardTheme.primary_color,
                  lineHeight: 1.2,
                }}
              >
                Estadísticas
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "stretch", sm: "center" },
                  gap: 1,
                }}
              >
                <TextField
                  type="date"
                  size="small"
                  label="Desde"
                  value={desde}
                  onChange={(event) => setDesde(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    width: { xs: "100%", sm: 160 },
                    ...dashboardTheme.inputStyles,
                  }}
                />

                <TextField
                  type="date"
                  size="small"
                  label="Hasta"
                  value={hasta}
                  onChange={(event) => setHasta(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    width: { xs: "100%", sm: 160 },
                    ...dashboardTheme.inputStyles,
                  }}
                />
              </Box>
            </Box>

            <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Total facturado"
                  value={`$${formatMoney(resumen.total_facturado)}`}
                  dashboardTheme={dashboardTheme}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Total en caja"
                  value={`$${formatMoney(resumen.total_caja_ventas)}`}
                  dashboardTheme={dashboardTheme}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Efectivo"
                  value={`$${formatMoney(resumen.total_efectivo)}`}
                  dashboardTheme={dashboardTheme}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Transferencia"
                  value={`$${formatMoney(resumen.total_transferencia)}`}
                  dashboardTheme={dashboardTheme}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Órdenes cobradas"
                  value={formatNumber(resumen.ordenes_cobradas)}
                  dashboardTheme={dashboardTheme}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Órdenes facturadas"
                  value={formatNumber(resumen.ordenes_facturadas)}
                  dashboardTheme={dashboardTheme}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Cajas abiertas"
                  value={formatNumber(resumen.cajas_abiertas)}
                  dashboardTheme={dashboardTheme}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Diferencia factura/caja"
                  value={`$${formatMoney(resumen.diferencia_facturado_caja)}`}
                  dashboardTheme={dashboardTheme}
                />
              </Grid>
            </Grid>

            <Grid container spacing={1.5}>
              <Grid item xs={12} lg={8}>
                <ChartCard title="Facturación y caja por día" dashboardTheme={dashboardTheme} height={320}>
                  {ventasComparadas.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={ventasComparadas}>
                        <CartesianGrid stroke={gridColor} vertical={false} />
                        <XAxis
                          dataKey="fecha"
                          tick={{ fill: secondaryColor, fontSize: 12 }}
                          axisLine={{ stroke: gridColor }}
                          tickLine={{ stroke: gridColor }}
                        />
                        <YAxis
                          tick={{ fill: secondaryColor, fontSize: 12 }}
                          axisLine={{ stroke: gridColor }}
                          tickLine={{ stroke: gridColor }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Line
                          type="monotone"
                          dataKey="totalFacturado"
                          name="Facturado"
                          stroke={chartColor}
                          strokeWidth={3}
                          dot={{ r: 3 }}
                          activeDot={{ r: 5 }}
                        />
                        <Line
                          type="monotone"
                          dataKey="totalCaja"
                          name="Caja"
                          stroke={secondaryColor}
                          strokeWidth={3}
                          dot={{ r: 3 }}
                          activeDot={{ r: 5 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    emptyBox
                  )}
                </ChartCard>
              </Grid>

              <Grid item xs={12} lg={4}>
                <ChartCard title="Formas de pago" dashboardTheme={dashboardTheme} height={320}>
                  {(data?.pagos_por_forma || []).length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Tooltip content={<CustomTooltip />} />
                        <Pie
                          data={data.pagos_por_forma}
                          dataKey="total"
                          nameKey="forma_pago"
                          innerRadius={isMobile ? 54 : 66}
                          outerRadius={isMobile ? 92 : 112}
                          paddingAngle={3}
                        >
                          {data.pagos_por_forma.map((entry, index) => (
                            <Cell key={entry.forma_pago} fill={colors[index % colors.length]} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    emptyBox
                  )}
                </ChartCard>
              </Grid>

              <Grid item xs={12}>
                <ChartCard title="Órdenes por estado" dashboardTheme={dashboardTheme} height={310}>
                  {(data?.ordenes_por_estado || []).length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.ordenes_por_estado}>
                        <CartesianGrid stroke={gridColor} vertical={false} />
                        <XAxis
                          dataKey="estado"
                          tick={{ fill: secondaryColor, fontSize: 12 }}
                          axisLine={{ stroke: gridColor }}
                          tickLine={{ stroke: gridColor }}
                        />
                        <YAxis
                          tick={{ fill: secondaryColor, fontSize: 12 }}
                          axisLine={{ stroke: gridColor }}
                          tickLine={{ stroke: gridColor }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="cantidad" name="Cantidad" radius={[10, 10, 0, 0]}>
                          {data.ordenes_por_estado.map((entry, index) => (
                            <Cell key={`${entry.estado}-${index}`} fill={colors[index % colors.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    emptyBox
                  )}
                </ChartCard>
              </Grid>

              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: "22px",
                    border: `1px solid ${dashboardTheme.border_color}`,
                    bgcolor: dashboardTheme.card_background,
                    overflow: "hidden",
                    boxShadow: "none",
                  }}
                >
                  <Box
                    sx={{
                      px: { xs: 1.6, md: 2.2 },
                      py: 1.8,
                      borderBottom: `1px solid ${dashboardTheme.border_color}`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: 17, md: 19 },
                        fontWeight: 700,
                        color: dashboardTheme.text_color,
                        lineHeight: 1.2,
                      }}
                    >
                      Resumen del periodo
                    </Typography>
                  </Box>

                  <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: dashboardTheme.table_header_color }}>
                          <TableCell sx={dashboardTheme.tableStyles.headCell}>Concepto</TableCell>
                          <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Valor</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        <TableRow sx={dashboardTheme.tableStyles.bodyRow}>
                          <TableCell sx={tableCellSx}>Facturas emitidas</TableCell>
                          <TableCell align="right" sx={tableCellSx}>{formatNumber(resumen.facturas)}</TableCell>
                        </TableRow>

                        <TableRow sx={dashboardTheme.tableStyles.bodyRow}>
                          <TableCell sx={tableCellSx}>Órdenes</TableCell>
                          <TableCell align="right" sx={tableCellSx}>{formatNumber(resumen.ordenes)}</TableCell>
                        </TableRow>

                        <TableRow sx={dashboardTheme.tableStyles.bodyRow}>
                          <TableCell sx={tableCellSx}>Pagos registrados</TableCell>
                          <TableCell align="right" sx={tableCellSx}>{formatNumber(resumen.pagos)}</TableCell>
                        </TableRow>

                        <TableRow sx={dashboardTheme.tableStyles.bodyRow}>
                          <TableCell sx={tableCellSx}>Ingresos manuales</TableCell>
                          <TableCell align="right" sx={tableCellSx}>${formatMoney(resumen.total_ingresos_manuales)}</TableCell>
                        </TableRow>

                        <TableRow sx={dashboardTheme.tableStyles.bodyRow}>
                          <TableCell sx={tableCellSx}>Egresos manuales</TableCell>
                          <TableCell align="right" sx={tableCellSx}>${formatMoney(resumen.total_egresos_manuales)}</TableCell>
                        </TableRow>

                        <TableRow sx={dashboardTheme.tableStyles.bodyRow}>
                          <TableCell sx={tableCellSx}>Cajas cerradas</TableCell>
                          <TableCell align="right" sx={tableCellSx}>{formatNumber(resumen.cajas_cerradas)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}